import React, { useState } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { limitEditionIntro, plainReview, reviewRange, excerpt } from './generated/model';
import { safeArtworkUrl } from './webSvg';
function Cover({ record, index }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: record.album.id,
  });
  return (
    <button
      type="button"
      className="pp-edition-sort"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      aria-label={`${index + 1}. ${record.album.title}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <img src={safeArtworkUrl(record.album.imageUrl)} alt={record.album.title} />
    </button>
  );
}
export default function EditionCustomize({
  p,
  set,
  albums,
  edition,
  quote,
  data,
  copy,
  save,
  busy,
  notice,
}) {
  const [albumPicker, setAlbumPicker] = useState(false),
    [reviewPicker, setReviewPicker] = useState(false),
    [source, setSource] = useState(false),
    [search, setSearch] = useState(''),
    [limit, setLimit] = useState(6),
    [reviewLimit, setReviewLimit] = useState(3);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const rows = edition.records.filter((r) =>
    `${r.album.title} ${r.artist?.name ?? ''}`
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase()),
  );
  const btn = (label, click, disabled = false) => (
    <button type="button" onClick={click} disabled={busy || disabled}>
      {label}
    </button>
  );
  return (
    <fieldset className="pp-edition-customize" disabled={busy}>
      <label>
        <strong>{copy('음악 명함 제목 문구', 'Music card heading')}</strong>
        <input
          value={p.cardHeading}
          onChange={(e) => set({ cardHeading: limitEditionIntro(e.target.value) })}
        />
      </label>
      <label>
        <strong>{copy('음악 명함 소개', 'Music card introduction')}</strong>
        <textarea
          value={p.intro}
          onChange={(e) => set({ intro: limitEditionIntro(e.target.value) })}
        />
      </label>
      <small>
        {copy('최대 두 줄 · 비워둘 수도 있어요.', 'Up to two lines · blank is welcome.')}
      </small>
      <label className="pp-edition-switch">
        {copy('닉네임 표시', 'Show nickname')}
        <input
          type="checkbox"
          checked={p.showName}
          onChange={(e) => set({ showName: e.target.checked })}
        />
      </label>
      <label className="pp-edition-switch">
        {copy('하단 평가·리뷰 개수 표시', 'Show footer record counts')}
        <input
          type="checkbox"
          checked={p.showStats}
          onChange={(e) => set({ showStats: e.target.checked })}
        />
      </label>
      <h3>{copy('음악 명함에 담을 앨범', 'Albums for my music card')}</h3>
      <small>
        {copy(
          '커버를 길게 눌러 원하는 자리로 옮겨주세요. 최대 9장을 담을 수 있어요. 키보드에서는 Space로 선택하고 방향키로 이동합니다.',
          'Hold a cover and drag it to its new position. Choose up to 9 albums. With a keyboard, press Space then arrow keys.',
        )}
      </small>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (over && over.id !== active.id) {
            const ids = albums.map((a) => a.album.id);
            set({ selectedIds: arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)) });
          }
        }}
      >
        <SortableContext items={albums.map((a) => a.album.id)} strategy={rectSortingStrategy}>
          <div className="pp-edition-grid">
            {albums.map((r, i) => (
              <Cover key={r.album.id} record={r} index={i} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="pp-edition-options">
        {btn(copy('앨범 고르기', 'Choose albums'), () => setAlbumPicker(!albumPicker))}
        {btn(copy('추천 구성으로 되돌리기', 'Restore recommended albums'), () =>
          set({ selectedIds: null }),
        )}
      </div>
      <small>
        {copy(
          '높게 평가한 앨범과 최근 4점 이상 준 앨범을 중심으로, 아티스트가 겹치지 않게 최대 9장을 골라요. 다른 카드의 통계는 바뀌지 않아요.',
          'Up to 9 albums, prioritizing high ratings and a recent 4+ favorite, with different artists first. Other cards’ statistics stay the same.',
        )}
      </small>
      {albumPicker && (
        <>
          <label>
            {copy('내 평가 앨범 검색', 'Search my rated albums')}
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setLimit(6);
              }}
            />
          </label>
          {rows.slice(0, limit).map((r) => {
            const selected = albums.some((a) => a.album.id === r.album.id);
            return (
              <button
                type="button"
                className="pp-edition-album"
                role="checkbox"
                aria-checked={selected}
                aria-label={r.album.title}
                key={r.album.id}
                onClick={() => {
                  const ids = albums.map((a) => a.album.id);
                  if (!selected && ids.length >= 9) {
                    notice(copy('최대 9장까지 선택할 수 있어요', 'Select up to 9 albums'));
                    return;
                  }
                  set({
                    selectedIds: selected
                      ? ids.filter((id) => id !== r.album.id)
                      : [...ids, r.album.id],
                  });
                }}
              >
                <img src={safeArtworkUrl(r.album.imageUrl)} alt="" />
                <span>
                  {r.album.title}
                  <small>{r.artist?.name}</small>
                </span>
                <span aria-hidden="true">{selected ? '✓' : '+'}</span>
              </button>
            );
          })}
          {rows.length > limit &&
            btn(copy('앨범 6개 더 보기', 'Show 6 more albums'), () => setLimit(limit + 6))}
          {!rows.length && <p>{copy('검색 결과가 없어요.', 'No results.')}</p>}
        </>
      )}
      {!!edition.reviews.length && (
        <>
          <h3>{copy('내 리뷰 문장', 'My review excerpt')}</h3>
          <p>{quote?.album.title}</p>
          {btn(copy('다른 리뷰 고르기', 'Choose another review'), () =>
            setReviewPicker(!reviewPicker),
          )}
          {reviewPicker && (
            <>
              {edition.reviews.slice(0, reviewLimit).map((r) => (
                <button
                  type="button"
                  key={r.review.id}
                  aria-pressed={quote?.review.id === r.review.id}
                  onClick={() => set({ quoteId: r.review.id, quoteStart: 0, quoteLength: 0 })}
                >
                  {excerpt(r.review.content)}
                  <small>{r.album.title}</small>
                </button>
              ))}
              {reviewLimit < edition.reviews.length &&
                btn(copy('리뷰 3개 더 보기', 'Show 3 more reviews'), () =>
                  setReviewLimit(reviewLimit + 3),
                )}
            </>
          )}
          {quote && (
            <>
              {btn(copy('원문에서 문장 직접 고르기', 'Select words in the original'), () =>
                setSource(!source),
              )}
              {source && (
                <>
                  <small>
                    {copy(
                      '원문에서 최대 110자(소장 기본형 70자)를 선택해주세요.',
                      'Select up to 110 characters (70 on pocket editorial cards).',
                    )}
                  </small>
                  <textarea
                    aria-label={copy(
                      '리뷰 원문에서 문장 선택',
                      'Select an excerpt in the original review',
                    )}
                    readOnly
                    value={plainReview(quote.review.content)}
                    onSelect={(e) => {
                      const { value, selectionStart: start, selectionEnd: end } = e.target;
                      if (end > start)
                        set({
                          quoteStart: Array.from(value.slice(0, start)).length,
                          quoteLength: Math.min(110, Array.from(value.slice(start, end)).length),
                        });
                    }}
                  />
                </>
              )}
              <small>{copy('카드에 담길 문장', 'Card excerpt')}</small>
              <p>
                {p.template === 'poster' && !p.quoteLength
                  ? Array.from(plainReview(quote.review.content)).slice(p.quoteStart).join('')
                  : reviewRange(
                      quote.review.content,
                      p.quoteStart,
                      Math.min(p.quoteLength || 110, p.format === 'card' ? 70 : 110),
                    )}
              </p>
              {!!(p.quoteStart || p.quoteLength) &&
                btn(copy('처음부터 자동으로 담기', 'Fit automatically from the start'), () =>
                  set({ quoteStart: 0, quoteLength: 0 }),
                )}
            </>
          )}
        </>
      )}
      {edition.topArtist && (
        <>
          <h3>{copy('아티스트 입문 추천 앨범', 'My artist introduction pick')}</h3>
          <div className="pp-edition-options">
            {edition.records
              .filter(
                (r) =>
                  r.artist &&
                  `${r.artist.artistSource}:${r.artist.sourceId || r.artist.name}` ===
                    edition.topArtist.key,
              )
              .map((r, i) => (
                <button
                  type="button"
                  key={r.album.id}
                  aria-pressed={
                    p.artistIntroId === r.album.id || (p.artistIntroId === null && i === 0)
                  }
                  onClick={() => set({ artistIntroId: r.album.id })}
                >
                  {r.album.title}
                </button>
              ))}
          </div>
        </>
      )}
      <h3>{copy('친구와 취향 교집합', 'Compare with a friend')}</h3>
      <small>
        {copy(
          '닉네임을 직접 입력한 뒤 조회하면 공개된 평가만 비교합니다. 상대에게 알림을 보내지 않으며, 공유 전 상대의 닉네임과 평가가 포함되는지 확인해주세요.',
          'Enter a nickname to compare public ratings only. No notification is sent. Check the other person’s name and ratings before sharing.',
        )}
      </small>
      <label>
        {copy('비교할 공개 닉네임', 'Public nickname to compare')}
        <input
          autoCapitalize="none"
          value={p.friendInput}
          onChange={(e) =>
            set({
              friendInput: Array.from(e.target.value).slice(0, 40).join(''),
              friendNickname: '',
            })
          }
        />
      </label>
      {btn(
        copy('공개 평가 조회', 'Load public ratings'),
        () =>
          p.friendNickname === p.friendInput.trim()
            ? data.friend.refetch()
            : set({ friendNickname: p.friendInput.trim() }),
        !p.friendInput.trim() || p.friendInput.trim() === data.nickname || data.friend.isFetching,
      )}
      {data.friend.isFetching && (
        <p role="status">{copy('공개 평가를 불러오고 있어요.', 'Loading public ratings.')}</p>
      )}
      {data.friend.isError && (
        <p role="alert">
          {copy(
            '공개 평가를 불러오지 못했어요. 닉네임을 확인하고 다시 조회해주세요.',
            'Could not load public ratings. Check the nickname and retry.',
          )}
        </p>
      )}
      {data.comparison &&
        btn(
          copy(
            `교집합 ${data.comparison.count}장 · 비교 장면 보기`,
            `${data.comparison.count} shared albums · View comparison`,
          ),
          () => data.jump('duet'),
        )}
      <small>
        {copy(
          '저장 버튼을 누르면 이 계정·브라우저에 저장돼요. 단어와 페스티벌 라인업은 내 기록에서 자동으로 정해져요.',
          'Save settings for this account and browser. Words and the festival lineup are chosen automatically from your records.',
        )}
      </small>
      <button type="button" className="pp-edition-primary" onClick={save}>
        {copy('현재 꾸미기 저장', 'Save current settings')}
      </button>
    </fieldset>
  );
}
